import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, Typography, Button, Paper, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, TableContainer, Card, CardContent, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Accordion, AccordionSummary, AccordionDetails, Alert } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFileExcel, faFilter, faCalendarAlt, faSyncAlt, faChartPie, faMoneyBillWave, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import {
    applyDateFormatToColumnFromRow,
    EXCEL_DATE_FORMAT,
    toExcelSerialDate,
} from '../../../../utils/reportExcelExport';
import TablePaginationActions from '../../../../base-components/TablePaginationActions';
import { isDynamicStaffRole } from '../../../../utils/roleAccess';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const EMPTY_MEMBER_SUMMARY = {
    maleMembers: 0,
    femaleMembers: 0,
    totalMembers: 0,
    personalCoachingMembers: 0,
    trainersWithMembers: []
};

function IncomeBreakdownReport() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [logs, setLogs] = useState([]);
    const [summary, setSummary] = useState({
        basicTotalIncome: 0,
        adjustedTotalIncome: 0,
        membershipFeeIncome: 0,
        registrationFeeIncome: 0,
        personalTrainingIncome: 0,
        coachSharePayable: 0,
        otherIncome: 0
    });
    const [totalLogs, setTotalLogs] = useState(0);
    const [memberSummary, setMemberSummary] = useState(EMPTY_MEMBER_SUMMARY);
    const [fetchError, setFetchError] = useState('');
    const [trainerModalOpen, setTrainerModalOpen] = useState(false);
    const [expandedTrainerId, setExpandedTrainerId] = useState(false);

    const [role, setRole] = useState('');
    const [location_id, setLocationID] = useState('');
    const [company_id, setCompanyID] = useState('');

    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(0);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [incomeType, setIncomeType] = useState("All");
    const [paymentMethod, setPaymentMethod] = useState("All");
    const [hasBankIntegration, setHasBankIntegration] = useState(false);

    const tableColSpan = hasBankIntegration ? 11 : 9;

    useEffect(() => {
        const sessionStr = sessionStorage.getItem('user-info');
        if (sessionStr) {
            const session = JSON.parse(sessionStr);
            setRole(session.user.role);
            setCompanyID(session.user.company_id);
            setLocationID(session.user.location_id);
            setHasBankIntegration(session.user.company?.has_bank_integration || false);
        } else {
            navigate('/login');
        }
    }, [navigate]);

    const fetchReportData = useCallback(async (
        currentPage = page,
        currentLimit = rowsPerPage,
        currentSearch = searchQuery,
        currentFrom = fromDate,
        currentTo = toDate,
        currentIncomeType = incomeType,
        currentPaymentMethod = paymentMethod
    ) => {
        setLoading(true);
        setFetchError('');
        try {
            let params = new URLSearchParams();
            if (role === 'Admin') params.append('company_id', company_id);
            if (role === 'ClubAdmin') params.append('location_id', location_id);
            else if (isDynamicStaffRole(role)) params.append('location_id', location_id);

            params.append('page', currentPage + 1);
            params.append('limit', currentLimit);

            if (currentSearch) params.append('search', currentSearch);
            if (currentFrom) params.append('from_date', currentFrom);
            if (currentTo) params.append('to_date', currentTo);
            if (currentIncomeType && currentIncomeType !== 'All') params.append('income_type', currentIncomeType);
            if (currentPaymentMethod && currentPaymentMethod !== 'All') params.append('payment_method', currentPaymentMethod);

            const response = await fetch(`${BASE_URL}reports/income/breakdown?${params.toString()}`);
            const data = await response.json();
            if (response.ok) {
                setLogs(data.logs);
                setTotalLogs(data.totalLogs);
                setSummary(data.summary);
                setMemberSummary(data.memberSummary || EMPTY_MEMBER_SUMMARY);
                setHasBankIntegration(data.company_has_bank_integration === true);
            } else {
                setFetchError(data.message || 'Failed to load income breakdown report.');
            }
        } catch (error) {
            console.error("Error fetching income report:", error);
            setFetchError('Failed to load income breakdown report. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [role, company_id, location_id, page, rowsPerPage, searchQuery, fromDate, toDate, incomeType, paymentMethod]);

    useEffect(() => {
        if (role) {
            fetchReportData();
        }
    }, [role, page, rowsPerPage, fetchReportData]);

    const handleFilter = () => {
        setPage(0);
        fetchReportData(0, rowsPerPage);
    };

    const handleClear = () => {
        setFromDate("");
        setToDate("");
        setSearchQuery("");
        setIncomeType("All");
        setPaymentMethod("All");
        setPage(0);
        fetchReportData(0, rowsPerPage, "", "", "", "All", "All");
    };

    const handleExportExcel = async () => {
        setExporting(true);
        try {
            let params = new URLSearchParams();
            if (role === 'Admin') params.append('company_id', company_id);
            if (role === 'ClubAdmin') params.append('location_id', location_id);
            else if (isDynamicStaffRole(role)) params.append('location_id', location_id);
            params.append('page', 1);
            params.append('limit', 1000000); // Fetch all
            if (searchQuery) params.append('search', searchQuery);
            if (fromDate) params.append('from_date', fromDate);
            if (toDate) params.append('to_date', toDate);
            if (incomeType !== 'All') params.append('income_type', incomeType);
            if (paymentMethod !== 'All') params.append('payment_method', paymentMethod);

            const response = await fetch(`${BASE_URL}reports/income/breakdown?${params.toString()}`);
            const data = await response.json();

            if (!response.ok) throw new Error("Export data fetch failed");

            // Format Summary Data as Array of Arrays
            const aoa = [];

            // 1. Statistics
            aoa.push(['Statistical Metric', 'Amount']);
            aoa.push(['Basic Total Income', data.summary.basicTotalIncome]);
            aoa.push(['Adjusted Total Income', data.summary.adjustedTotalIncome]);
            aoa.push(['Membership Fee Income', data.summary.membershipFeeIncome]);
            aoa.push(['Registration Fee Income', data.summary.registrationFeeIncome]);
            aoa.push(['Personal Training Income', data.summary.personalTrainingIncome]);
            aoa.push(['Coach Share Payable', data.summary.coachSharePayable]);
            aoa.push(['Other Income', data.summary.otherIncome]);

            const ms = data.memberSummary || EMPTY_MEMBER_SUMMARY;
            aoa.push([]);
            aoa.push(['Member Statistics (active in period)', 'Count']);
            aoa.push(['Male Members', ms.maleMembers || 0]);
            aoa.push(['Female Members', ms.femaleMembers || 0]);
            aoa.push(['Total Members', ms.totalMembers || 0]);
            aoa.push(['Personal Coaching Members', ms.personalCoachingMembers || 0]);

            if (ms.trainersWithMembers?.length > 0) {
                aoa.push([]);
                aoa.push(['Trainer', 'Member Name', 'Member ID', 'Phone']);
                ms.trainersWithMembers.forEach((trainer) => {
                    if (trainer.members?.length > 0) {
                        trainer.members.forEach((member) => {
                            aoa.push([
                                trainer.trainerName,
                                member.name || 'N/A',
                                member.member_id ?? 'N/A',
                                member.phone || 'N/A'
                            ]);
                        });
                    } else {
                        aoa.push([trainer.trainerName, 'No members', '', '']);
                    }
                });
            }

            // 2. Gap row
            aoa.push([]);

            const exportHasBank = data.company_has_bank_integration === true;

            // 3. Details Table Headers
            const detailHeaders = [
                'Date',
                'Member Name',
                'Payment Method',
            ];
            if (exportHasBank) {
                detailHeaders.push('Bank Name', 'Bank GL');
            }
            detailHeaders.push(
                'Paid Amount',
                'Payment Reference',
                'Membership Fee',
                'Registration Fee',
                'Trainer Fee',
                'Other Income',
                'Other Income Type'
            );
            aoa.push(detailHeaders);

            // 4. Detail Table Data
            data.logs.forEach(log => {
                const row = [
                    toExcelSerialDate(log.date) ?? '',
                    log.member_id?.name || 'N/A',
                    log.payment_method,
                ];
                if (exportHasBank) {
                    row.push(log.bank_name || '—', log.bank_gl || '—');
                }
                row.push(
                    log.paid_amount,
                    log.reference_number || '',
                    (log.breakdown.membership_fee || 0).toFixed(2),
                    (log.breakdown.registration_fee || 0).toFixed(2),
                    (log.breakdown.trainer_fee || 0).toFixed(2),
                    (log.breakdown.other_income || 0).toFixed(2),
                    log.breakdown.other_income_name || 'N/A'
                );
                aoa.push(row);
            });

            const worksheet = XLSX.utils.aoa_to_sheet(aoa);
            const detailHeaderRowIndex = aoa.findIndex((row) => row[0] === 'Date');
            if (detailHeaderRowIndex >= 0) {
                applyDateFormatToColumnFromRow(worksheet, 0, detailHeaderRowIndex + 1, EXCEL_DATE_FORMAT);
            }
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Income Breakdown");

            const wscols = [{ wch: 15 }, { wch: 25 }, { wch: 15 }];
            if (exportHasBank) {
                wscols.push({ wch: 18 }, { wch: 18 });
            }
            wscols.push(
                { wch: 15 },
                { wch: 20 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 20 }
            );
            worksheet['!cols'] = wscols;

            XLSX.writeFile(workbook, `Income_Breakdown_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setExporting(false);
        }
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
      <>

        
            <div className="p-4 md:p-8 bg-gx-canvas min-h-screen">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                    <Box>
                        <Typography variant="h4" className="font-extrabold text-gx-navy flex items-center gap-3">
                            <div className="bg-gx-navy p-3 rounded-gx shadow-gx">
                                <FontAwesomeIcon icon={faChartPie} className="text-white text-2xl" />
                            </div>
                            Income Breakdown Report
                        </Typography>
                        <Typography variant="body2" className="text-gx-ink-soft mt-1 ml-1">
                            Analyze revenue streams including memberships, registrations, training, and other income.
                        </Typography>
                    </Box>

                    <div className="flex gap-3">
                        <Button
                            variant="outlined"
                            startIcon={<FontAwesomeIcon icon={faSyncAlt} />}
                            onClick={() => fetchReportData()}
                            className="border-gx-navy text-gx-navy hover:bg-gx-navy/5 px-4 py-2 rounded-xl capitalize font-bold h-[45px]"
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FontAwesomeIcon icon={faFileExcel} />}
                            onClick={handleExportExcel}
                            disabled={exporting}
                            className="bg-[#107c41] hover:bg-[#0e6b38] text-white px-6 py-2 rounded-xl capitalize font-bold shadow-md h-[45px]"
                        >
                            {exporting ? 'Exporting...' : 'Export Excel'}
                        </Button>
                    </div>
                </div>

                {/* Filters Section */}
                <Paper elevation={0} className="p-6 rounded-2xl border border-gx-border mb-8 bg-white shadow-gx">
                    <div className="flex items-center gap-2 mb-6 border-b border-gx-border pb-4">
                        <FontAwesomeIcon icon={faFilter} className="text-gx-red" />
                        <Typography className="font-bold text-gx-ink">Filter Controls</Typography>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gx-ink-soft flex items-center gap-2">
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-[10px]" />
                                From Date
                            </label>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="w-full border border-gx-border rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-[#D71920]/20 focus:border-gx-red transition-all bg-gx-canvas/50"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gx-ink-soft flex items-center gap-2">
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-[10px]" />
                                To Date
                            </label>
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                min={fromDate}
                                className="w-full border border-gx-border rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-[#D71920]/20 focus:border-gx-red transition-all bg-gx-canvas/50"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gx-ink-soft flex items-center gap-2">
                                <FontAwesomeIcon icon={faFilter} className="text-[10px]" />
                                Income Type
                            </label>
                            <select
                                value={incomeType}
                                onChange={(e) => setIncomeType(e.target.value)}
                                className="w-full h-[46px] border border-gx-border rounded-xl p-3 pl-4 text-sm outline-none focus:ring-2 focus:ring-[#D71920]/20 focus:border-gx-red transition-all bg-gx-canvas/50 appearance-none"
                            >
                                <option value="All">All Incomes</option>
                                <option value="Membership Fee">Membership Fee</option>
                                <option value="Registration Fee">Registration Fee</option>
                                <option value="Personal Training">Personal Training</option>
                                <option value="Other Income">Other Income</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gx-ink-soft flex items-center gap-2">
                                <FontAwesomeIcon icon={faFilter} className="text-[10px]" />
                                Payment Method
                            </label>
                            <select
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                className="w-full h-[46px] border border-gx-border rounded-xl p-3 pl-4 text-sm outline-none focus:ring-2 focus:ring-[#D71920]/20 focus:border-gx-red transition-all bg-gx-canvas/50 appearance-none"
                            >
                                <option value="All">All Methods</option>
                                <option value="Cash">Cash</option>
                                <option value="Card">Card</option>
                                <option value="Bank Transfer">Bank Transfer</option>
                                <option value="Online">Online</option>
                                <option value="Jazzcash">Jazzcash</option>
                                <option value="Wallet">Wallet</option>
                                <option value="Cheque">Cheque</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gx-ink-soft flex items-center gap-2">
                                <FontAwesomeIcon icon={faSearch} className="text-[10px]" />
                                Search Member
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search by name or ID..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleFilter()}
                                    className="w-full border border-gx-border rounded-xl p-3 pl-11 text-sm outline-none focus:ring-2 focus:ring-[#D71920]/20 focus:border-gx-red transition-all bg-gx-canvas/50"
                                />
                                <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-[14px] text-gx-ink-soft" />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8 pt-6 border-t border-gx-border">
                        <Button
                            variant="text"
                            onClick={handleClear}
                            className="text-gx-ink-soft hover:text-gx-ink-soft font-bold px-6 order-2 sm:order-1"
                        >
                            Reset Defaults
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleFilter}
                            className="bg-gx-red hover:bg-[#b5161b] text-white px-10 py-2.5 rounded-xl font-bold shadow-lg shadow-[#D71920]/20 transition-all hover:-translate-y-0.5 order-1 sm:order-2"
                        >
                            Apply Filters
                        </Button>
                    </div>
                </Paper>

                {/* Summary Section */}
                <Grid container spacing={3} className="mb-8">
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-2xl border border-gx-border shadow-gx">
                            <CardContent className="flex flex-col max-w-full">
                                <Typography className="text-gx-ink-soft font-bold uppercase text-xs mb-2 truncate">Basic Total Income</Typography>
                                <Typography className="text-[10px] text-gx-ink-soft mb-1">Includes fee collections and paid POS</Typography>
                                <Typography className="text-gx-navy font-extrabold text-2xl truncate">{(summary.basicTotalIncome || 0).toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-2xl border border-gx-border shadow-gx">
                            <CardContent className="flex flex-col max-w-full">
                                <Typography className="text-gx-ink-soft font-bold uppercase text-xs mb-2 truncate">Adjusted Total Income</Typography>
                                <Typography className="text-gx-navy font-extrabold text-2xl truncate">{(summary.adjustedTotalIncome || 0).toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-2xl border border-gx-border shadow-gx">
                            <CardContent className="flex flex-col max-w-full">
                                <Typography className="text-gx-ink-soft font-bold uppercase text-xs mb-2 truncate">Membership Fee Income</Typography>
                                <Typography className="text-gx-navy font-extrabold text-2xl truncate">{(summary.membershipFeeIncome || 0).toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-2xl border border-gx-border shadow-gx">
                            <CardContent className="flex flex-col max-w-full">
                                <Typography className="text-gx-ink-soft font-bold uppercase text-xs mb-2 truncate">Registration Fee Income</Typography>
                                <Typography className="text-gx-navy font-extrabold text-2xl truncate">{(summary.registrationFeeIncome || 0).toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-2xl border border-gx-border shadow-gx">
                            <CardContent className="flex flex-col max-w-full">
                                <Typography className="text-gx-ink-soft font-bold uppercase text-xs mb-2 truncate">Personal Training</Typography>
                                <Typography className="text-gx-navy font-extrabold text-2xl truncate">{(summary.personalTrainingIncome || 0).toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-2xl border border-gx-border shadow-gx">
                            <CardContent className="flex flex-col max-w-full">
                                <Typography className="text-gx-ink-soft font-bold uppercase text-xs mb-2 flex items-center gap-1 truncate"><FontAwesomeIcon icon={faMoneyBillWave} color="#D71920" /> Coach Share (<span className='lowercase'>payable</span>)</Typography>
                                <Typography className="text-gx-red font-extrabold text-2xl truncate">{(summary.coachSharePayable || 0).toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-2xl border border-gx-border shadow-gx">
                            <CardContent className="flex flex-col max-w-full">
                                <Typography className="text-gx-ink-soft font-bold uppercase text-xs mb-2 truncate">Other Income</Typography>
                                <Typography className="text-gx-navy font-extrabold text-2xl truncate">{(summary.otherIncome || 0).toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Typography variant="subtitle1" className="font-bold text-gx-navy mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon={faUsers} className="text-gx-red" />
                    Member Statistics (active members in period)
                </Typography>

                {fetchError && (
                    <Alert severity="error" className="mb-4 rounded-xl">
                        {fetchError}
                    </Alert>
                )}

                <Grid container spacing={3} className="mb-8">
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-2xl border border-gx-border shadow-gx">
                            <CardContent className="flex flex-col max-w-full">
                                <Typography className="text-gx-ink-soft font-bold uppercase text-xs mb-2 truncate">Male Members</Typography>
                                <Typography className="text-gx-navy font-extrabold text-2xl truncate">{(memberSummary.maleMembers || 0).toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-2xl border border-gx-border shadow-gx">
                            <CardContent className="flex flex-col max-w-full">
                                <Typography className="text-gx-ink-soft font-bold uppercase text-xs mb-2 truncate">Female Members</Typography>
                                <Typography className="text-gx-navy font-extrabold text-2xl truncate">{(memberSummary.femaleMembers || 0).toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            className="rounded-2xl border border-gx-border shadow-gx cursor-pointer transition-all hover:shadow-md hover:border-gx-red/30"
                            onClick={() => setTrainerModalOpen(true)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setTrainerModalOpen(true);
                                }
                            }}
                        >
                            <CardContent className="flex flex-col max-w-full">
                                <Typography className="text-gx-ink-soft font-bold uppercase text-xs mb-2 truncate">Personal Coaching Members</Typography>
                                <Typography className="text-gx-red font-extrabold text-2xl truncate">{(memberSummary.personalCoachingMembers || 0).toLocaleString()}</Typography>
                                <Typography className="text-[10px] text-gx-ink-soft mt-1 font-semibold">Click to view trainers (active coached members)</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className="rounded-2xl border border-gx-border shadow-gx">
                            <CardContent className="flex flex-col max-w-full">
                                <Typography className="text-gx-ink-soft font-bold uppercase text-xs mb-2 truncate">Total Members</Typography>
                                <Typography className="text-gx-navy font-extrabold text-2xl truncate">{(memberSummary.totalMembers || 0).toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Dialog
                    open={trainerModalOpen}
                    onClose={() => {
                        setTrainerModalOpen(false);
                        setExpandedTrainerId(false);
                    }}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle className="font-bold text-gx-navy">
                        Personal Coaching — Trainers &amp; Active Members
                    </DialogTitle>
                    <DialogContent dividers>
                        {memberSummary.trainersWithMembers?.length > 0 ? (
                            memberSummary.trainersWithMembers.map((trainer) => (
                                <Accordion
                                    key={trainer.trainerId}
                                    expanded={expandedTrainerId === trainer.trainerId}
                                    onChange={(_, isExpanded) =>
                                        setExpandedTrainerId(isExpanded ? trainer.trainerId : false)
                                    }
                                    className="mb-2 rounded-xl border border-gx-border shadow-none before:hidden"
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box className="flex items-center justify-between w-full pr-2">
                                            <Typography className="font-bold text-gx-navy">
                                                {trainer.trainerName}
                                            </Typography>
                                            <Typography className="text-xs font-bold text-gx-ink-soft bg-gx-canvas px-2 py-1 rounded-full">
                                                {trainer.members?.length || 0} member{(trainer.members?.length || 0) !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {trainer.members?.length > 0 ? (
                                            <Box component="ul" className="list-none m-0 p-0 space-y-2">
                                                {trainer.members.map((member) => (
                                                    <Box
                                                        key={member._id}
                                                        component="li"
                                                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 px-3 bg-gx-canvas rounded-lg"
                                                    >
                                                        <Typography className="font-semibold text-gx-navy text-sm">
                                                            {member.name || 'Unknown'}
                                                        </Typography>
                                                        <Typography className="text-xs text-gx-ink-soft">
                                                            ID: {member.member_id ?? 'N/A'}
                                                            {member.phone ? ` · ${member.phone}` : ''}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography className="text-gx-ink-soft text-sm">No members assigned.</Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            ))
                        ) : (
                            <Typography className="text-gx-ink-soft py-4 text-center">
                                No trainers with members in this period.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setTrainerModalOpen(false)} className="font-bold text-gx-navy">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Table Section */}
                {loading ? (
                    <div className="flex flex-col justify-center items-center py-32 gap-4">
                        <CircularProgress color="secondary" size={50} />
                        <Typography className="text-gx-ink-soft font-medium animate-pulse text-sm">Aggregating income data...</Typography>
                    </div>
                ) : (
                    <Paper elevation={0} className="rounded-2xl border border-gx-border bg-white shadow-gx transition-all hover:shadow-md">
                        <TableContainer className="rounded-2xl overflow-x-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                            <Table sx={{ minWidth: 900 }}>
                                <TableHead className="bg-[#fcfdfe]">
                                    <TableRow>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest pl-8 whitespace-nowrap">Date</TableCell>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap">Member Details</TableCell>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap">Method</TableCell>
                                        {hasBankIntegration && (
                                            <>
                                                <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap">Bank Name</TableCell>
                                                <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap">Bank GL</TableCell>
                                            </>
                                        )}
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap">Paid Amount</TableCell>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap">Payment Reference</TableCell>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap text-right">Membership Fee</TableCell>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap text-right">Reg. Fee</TableCell>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap text-right">Trainer Fee</TableCell>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest pr-8 whitespace-nowrap text-right">Other Income</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.length > 0 ? (
                                        logs.map((log) => (
                                            <TableRow key={log._id} hover className="transition-colors">
                                                <TableCell className="pl-8 py-5 border-b border-gx-border whitespace-nowrap">
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-gx-navy text-sm italic">{dayjs(log.date).format('DD MMM, YYYY')}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-5 border-b border-gx-border whitespace-nowrap">
                                                    <div>
                                                        <Typography variant="body2" className="font-bold text-gx-navy">
                                                            {log.member_id?.name || 'Walk-in / Unknown'}
                                                        </Typography>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-5 border-b border-gx-border whitespace-nowrap">
                                                    <span className={`px-2 py-1 rounded-sm text-[10px] font-extrabold uppercase bg-gx-canvas text-gx-ink-soft`}>
                                                        {log.payment_method}
                                                    </span>
                                                </TableCell>
                                                {hasBankIntegration && (
                                                    <>
                                                        <TableCell className="py-5 border-b border-gx-border whitespace-nowrap">
                                                            <Typography className="text-sm font-medium text-gx-navy">
                                                                {log.bank_name || '—'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell className="py-5 border-b border-gx-border whitespace-nowrap">
                                                            <Typography className="text-sm font-medium text-gx-navy">
                                                                {log.bank_gl || '—'}
                                                            </Typography>
                                                        </TableCell>
                                                    </>
                                                )}
                                                <TableCell className="py-5 border-b border-gx-border whitespace-nowrap">
                                                    <Typography className="font-bold text-sm text-gx-navy">{(log.paid_amount || 0).toLocaleString()}</Typography>
                                                </TableCell>
                                                <TableCell className="py-5 border-b border-gx-border whitespace-nowrap">
                                                    <Typography className="text-sm font-medium text-gx-navy">
                                                        {log.reference_number || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell className="py-5 border-b border-gx-border whitespace-nowrap text-right">
                                                    <Typography className="text-sm font-medium">{(log.breakdown.membership_fee || 0).toLocaleString()}</Typography>
                                                </TableCell>
                                                <TableCell className="py-5 border-b border-gx-border whitespace-nowrap text-right">
                                                    <Typography className="text-sm font-medium">{(log.breakdown.registration_fee || 0).toLocaleString()}</Typography>
                                                </TableCell>
                                                <TableCell className="py-5 border-b border-gx-border whitespace-nowrap text-right">
                                                    <Typography className="text-sm font-medium">{(log.breakdown.trainer_fee || 0).toLocaleString()}</Typography>
                                                </TableCell>
                                                <TableCell className="py-5 border-b border-gx-border pr-8 whitespace-nowrap text-right align-top">
                                                    <div className="flex flex-col items-end gap-1">
                                                        <Typography className="text-sm font-medium">{(log.breakdown.other_income || 0).toLocaleString()}</Typography>
                                                        {log.breakdown.other_income_name && (
                                                            <Typography className="text-[10px] text-gx-ink-soft bg-gx-canvas px-1.5 py-0.5 rounded-sm font-bold capitalize">
                                                                {log.breakdown.other_income_name.toLowerCase()}
                                                            </Typography>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={tableColSpan} align="center" className="py-24">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className="bg-gx-canvas p-6 rounded-full text-gx-ink-soft">
                                                        <FontAwesomeIcon icon={faChartPie} size="3x" />
                                                    </div>
                                                    <Typography className="text-gx-ink-soft font-bold tracking-tight">No income records found.</Typography>
                                                    <Button variant="text" size="small" onClick={handleClear} className="text-gx-red font-bold">Clear All Search Terms</Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TablePagination
                                            rowsPerPageOptions={[10, 25, 50]}
                                            colSpan={tableColSpan}
                                            count={totalLogs}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            ActionsComponent={TablePaginationActions}
                                            className="border-none"
                                        />
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}
            </div>

            <style jsx>{`
                .MuiTablePagination-root {
                    color: #64748b;
                    font-weight: 600;
                }
                .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows {
                    text-transform: uppercase;
                    font-size: 11px;
                    letter-spacing: 0.05em;
                }
            `}</style>
        
      </>
    );
}

export default IncomeBreakdownReport;
